import dayjs from "./dayjs";
import { TimeString } from "./hours";

export interface HolidayTimeSlot {
  open: TimeString;
  close: TimeString;
}

export interface HolidayHours {
  name: string;
  dateFrom: string; // ISO date string
  dateTo: string; // ISO date string
  hours: HolidayTimeSlot[];
  hoursOrdering: HolidayTimeSlot[] | null;
  greetingMessageOpen?: string;
  greetingMessageClosed?: string;
}

export interface HolidayHoursConfig {
  enabled: boolean;
  holidays: HolidayHours[];
}

// Backend API types
export interface HolidayTimeSlotResponse {
  open: string;
  close: string;
}

export interface HolidayHoursResponse {
  name: string;
  dateFrom: string;
  dateTo: string;
  hours: HolidayTimeSlotResponse[];
  hoursOrdering: HolidayTimeSlotResponse[] | null;
  greetingMessageOpen?: string;
  greetingMessageClosed?: string;
}

export interface HolidayHoursConfigResponse {
  enabled: boolean;
  holidays: HolidayHoursResponse[];
}

export interface HolidayTimeSlotRequest {
  open: string;
  close: string;
}

export interface HolidayHoursRequest {
  name: string;
  dateFrom: string;
  dateTo: string;
  hours: HolidayTimeSlotRequest[];
  hoursOrdering: HolidayTimeSlotRequest[] | null;
  greetingMessageOpen?: string;
  greetingMessageClosed?: string;
}

export interface HolidayHoursConfigRequest {
  enabled: boolean;
  holidays: HolidayHoursRequest[];
}

// Default empty config
export const emptyHolidayHoursConfig: HolidayHoursConfig = {
  enabled: true,
  holidays: [],
};

// Conversion functions
export function convertHolidayHoursToFrontend(
  holidayHours: HolidayHoursConfigResponse | null | undefined,
): HolidayHoursConfig {
  if (!holidayHours) {
    return emptyHolidayHoursConfig;
  }

  return {
    enabled: holidayHours.enabled,
    holidays: holidayHours.holidays.map((holiday) => ({
      name: holiday.name,
      dateFrom: holiday.dateFrom,
      dateTo: holiday.dateTo,
      // hours: mergeOvernightTimeSlots(holiday.hours),
      hours: holiday.hours as HolidayTimeSlot[],
      hoursOrdering: holiday.hoursOrdering as HolidayTimeSlot[] | null,
      // holiday.hoursOrdering === null
      //   ? null
      //   : mergeOvernightTimeSlots(holiday.hoursOrdering),
      greetingMessageOpen: holiday.greetingMessageOpen ?? "",
      greetingMessageClosed: holiday.greetingMessageClosed ?? "",
    })),
  };
}

// Helper function to check if a time slot spans overnight (close time is before open time)
function isOvernightSlot(slot: HolidayTimeSlot): boolean {
  return slot.close < slot.open;
}

/* NOTE: We do not allow overnight slots for MVP. */
// Helper function to split overnight slots into two slots: one ending at 23:59 and one starting at 00:00
// function splitOvernightTimeSlots(slots: HolidayTimeSlot[]): HolidayTimeSlot[] {
//   const result: HolidayTimeSlot[] = [];

//   for (const slot of slots) {
//     if (isOvernightSlot(slot)) {
//       // Split into two slots
//       result.push({ open: "00:00" as TimeString, close: slot.close });
//       result.push({ open: slot.open, close: "23:59" as TimeString });
//     } else {
//       result.push(slot);
//     }
//   }

//   return result;
// }

// // Helper function to merge adjacent midnight slots back into overnight slots
// function mergeOvernightTimeSlots(
//   slots: HolidayTimeSlotResponse[],
// ): HolidayTimeSlot[] {
//   if (slots.length === 0) {
//     return [];
//   }

//   // Sort by open time
//   const sorted = [...slots].sort((a, b) =>
//     a.open < b.open ? -1 : a.open > b.open ? 1 : 0,
//   );

//   // Check if we have the overnight pattern: first slot starts at 00:00 and last slot ends at 23:59
//   const firstSlot = sorted[0];
//   const lastSlot = sorted[sorted.length - 1];

//   if (
//     sorted.length >= 2 &&
//     firstSlot.open === "00:00" &&
//     lastSlot.close === "23:59"
//   ) {
//     // This is an overnight slot split pattern
//     // Merge first and last, keep the middle slots
//     const result: HolidayTimeSlot[] = [];

//     // Add the merged overnight slot
//     result.push({
//       open: lastSlot.open as TimeString,
//       close: firstSlot.close as TimeString,
//     });

//     // Add all the middle slots (if any)
//     for (let i = 1; i < sorted.length - 1; i++) {
//       result.push({
//         open: sorted[i].open as TimeString,
//         close: sorted[i].close as TimeString,
//       });
//     }

//     return result;
//   }

//   // No overnight pattern detected, return slots as-is
//   return sorted.map((slot) => ({
//     open: slot.open as TimeString,
//     close: slot.close as TimeString,
//   }));
// }

// Helper function to merge overlapping or adjacent time slots
function mergeTimeSlots(slots: HolidayTimeSlot[]): HolidayTimeSlotRequest[] {
  if (slots.length === 0) {
    return [];
  }

  // Sort by open time
  const sorted = [...slots].sort((a, b) =>
    a.open < b.open ? -1 : a.open > b.open ? 1 : 0,
  );

  const merged: HolidayTimeSlotRequest[] = [];
  let current = { open: sorted[0].open, close: sorted[0].close };

  for (let i = 1; i < sorted.length; i++) {
    const slot = sorted[i];

    // Check if current slot overlaps or is adjacent to the next slot
    if (slot.open <= current.close) {
      // Merge: extend current close time if the new slot ends later
      if (slot.close > current.close) {
        current.close = slot.close;
      }
    } else {
      // No overlap: push current and start a new one
      merged.push(current);
      current = { open: slot.open, close: slot.close };
    }
  }

  // Push the last slot
  merged.push(current);
  return merged;
}

export function convertHolidayHoursToBackend(
  holidayHours: HolidayHoursConfig | null | undefined,
): HolidayHoursConfigRequest | null {
  if (!holidayHours) {
    return null;
  }

  return {
    // TODO: Should this be exposed to the frontend as a toggle?
    enabled: holidayHours.enabled,
    holidays: holidayHours.holidays.map((holiday) => ({
      name: holiday.name,
      dateFrom: holiday.dateFrom,
      dateTo: holiday.dateTo,
      // hours: mergeTimeSlots(splitOvernightTimeSlots(holiday.hours)),
      hours: mergeTimeSlots(holiday.hours),
      hoursOrdering:
        holiday.hoursOrdering === null
          ? null
          : mergeTimeSlots(holiday.hoursOrdering),
      // holiday.hoursOrdering === null
      //   ? null
      //   : mergeTimeSlots(splitOvernightTimeSlots(holiday.hoursOrdering)),
      ...(holiday.greetingMessageOpen !== undefined && {
        greetingMessageOpen: holiday.greetingMessageOpen,
      }),
      ...(holiday.greetingMessageClosed !== undefined && {
        greetingMessageClosed: holiday.greetingMessageClosed,
      }),
    })),
  };
}

export function validateTimeSlot(slot: HolidayTimeSlot): string | null {
  if (!slot.open || !slot.close) {
    return "Invalid time slot: missing open or close time.";
  }
  if (isOvernightSlot(slot)) {
    return "Invalid time slot: overnight slots are not allowed in holiday hours.";
  }
  return null;
}

/**
 * Check if a holiday has passed.
 * A holiday is considered passed if the current date is after the dateTo (the day after dateTo).
 * @param dateTo - The end date of the holiday in ISO format
 * @returns true if the holiday has passed, false otherwise
 */
export function hasHolidayPassed(dateTo: string): boolean {
  const now = dayjs().startOf("day");
  const dayAfterEnd = dayjs(dateTo).add(1, "day").startOf("day");

  return now.isAfter(dayAfterEnd) || now.isSame(dayAfterEnd);
}

export function formatDate(isoDate: string): string {
  return dayjs(isoDate.split("T")[0]).format("MMM D, YYYY");
}
